import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Target, X } from "lucide-react";
import { Leaderboard } from "@/components/practice/Leaderboard";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader, PageShell } from "@/components/AppHeader";

const pct = (r) => `${Math.round(r * 100)}%`;

const Bars = ({ rows, label, testId }) => (
  <ul className="space-y-2" data-testid={testId}>
    {rows.map((r) => (
      <li key={r[label]} className="space-y-1">
        <div className="flex justify-between text-sm font-bold"><span>{r[label]}</span><span>{pct(r.accuracy)} · {r.attempts} tries</span></div>
        <div className="bar-track"><div className="bar-fill" style={{ width: pct(r.accuracy), background: r.accuracy < 0.5 ? "#dc2626" : r.accuracy < 0.75 ? "#f59e0b" : "#16a34a" }} /></div>
      </li>
    ))}
    {rows.length === 0 && <li className="text-sm font-semibold text-[var(--muted)]">No practice yet.</li>}
  </ul>
);

export default function PracticeStatsPage() {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [stats, setStats] = useState(null);
  const [drills, setDrills] = useState([]);

  const loadDrills = () => api.assignments(teamId).then(setDrills);
  useEffect(() => { api.team(teamId).then(setTeam); api.practiceStats(teamId).then(setStats); loadDrills(); }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps
  const assign = (m) => api.assign(teamId, { key: m.key, position: m.position, ball: m.ball || [] })
    .then(() => { toast.success(`Assigned to practice for ${m.position}`); loadDrills(); }, (e) => toast.error(formatApiError(e)));
  const unassign = (d) => api.unassign(teamId, d.id).then(loadDrills);
  const isAssigned = (m) => drills.some((d) => d.key === m.key && d.position === m.position);
  if (!team || !stats) return <div className="min-h-screen grid place-items-center font-heading text-2xl">Loading...</div>;
  const link = `${window.location.origin}/t/${team.join_code}/practice`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Practice link copied"), () => toast.message(link));

  return (
    <PageShell>
      <AppHeader><Link to="/coach" className="nav-btn" data-testid="back-to-teams-link"><ArrowLeft size={18} /> Teams</Link></AppHeader>
      <div className="team-banner">
        <div className="min-w-0">
          <p className="ctl-label">Practice report</p>
          <h1 className="font-heading text-2xl sm:text-4xl font-extrabold uppercase tracking-tight truncate" data-testid="stats-team-name">{team.name}</h1>
        </div>
        <button className="btn-ghost" data-testid="copy-practice-link-button" onClick={copy}><Copy size={18} /> Practice link</button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="card stat-box"><span className="ctl-label">Accuracy</span><b data-testid="stats-accuracy">{stats.total ? pct(stats.correct / stats.total) : "–"}</b></div>
        <div className="card stat-box"><span className="ctl-label">Attempts</span><b data-testid="stats-total">{stats.total}</b></div>
        <div className="card stat-box"><span className="ctl-label">Players</span><b data-testid="stats-players">{stats.players}/{stats.max_players}</b></div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Leaderboard data={stats.leaderboard} />
        <div className="card space-y-3" data-testid="assigned-drills">
          <h2 className="font-heading text-2xl font-bold uppercase">Assigned drills ({drills.length})</h2>
          <p className="text-sm font-medium text-[var(--muted)]">Players see these first when they open practice.</p>
          <ul className="space-y-2">
            {drills.map((d) => (
              <li key={d.id} className="custom-item !cursor-default flex items-start gap-2" data-testid="assigned-drill-item">
                <div className="flex-1 min-w-0">
                  <span className="chip">{d.position || "Everyone"}</span>
                  <p className="text-sm font-semibold mt-1">{d.explanation}</p>
                </div>
                <button className="icon-btn !min-h-[40px] !min-w-[40px]" data-testid="unassign-drill-button" aria-label="Remove drill" onClick={() => unassign(d)}><X size={18} /></button>
              </li>
            ))}
            {!drills.length && <li className="text-sm font-semibold text-[var(--muted)]">No drills yet. Assign a missed situation below, or use Assign on any play in the playbook.</li>}
          </ul>
        </div>
        <div className="card space-y-3">
          <h2 className="font-heading text-2xl font-bold uppercase">Most-missed situations</h2>
          <ul className="space-y-2" data-testid="stats-most-missed">
            {stats.most_missed.map((m) => (
              <li key={`${m.key}-${m.position}`} className="custom-item !cursor-default">
                <div className="flex flex-wrap gap-1.5 mb-1"><span className="chip">{m.position}</span><span className="chip">{m.misses} missed / {m.attempts}</span></div>
                <p className="text-sm font-semibold">{m.explanation}</p>
                <button className="btn-ghost !min-h-[40px] !text-sm mt-2" data-testid="assign-missed-button" disabled={isAssigned(m)} onClick={() => assign(m)}>
                  <Target size={16} /> {isAssigned(m) ? "Assigned" : `Assign to ${m.position} practice`}
                </button>
              </li>
            ))}
            {stats.most_missed.length === 0 && <li className="text-sm font-semibold text-[var(--muted)]">No misses yet. Share the practice link with your team.</li>}
          </ul>
        </div>
        <div className="card space-y-3">
          <h2 className="font-heading text-2xl font-bold uppercase">By situation type</h2>
          <Bars rows={stats.by_situation_type} label="type" testId="stats-by-type" />
        </div>
        <div className="card space-y-3">
          <h2 className="font-heading text-2xl font-bold uppercase">By position</h2>
          <Bars rows={stats.by_position} label="position" testId="stats-by-position" />
        </div>
        <div className="card space-y-3">
          <h2 className="font-heading text-2xl font-bold uppercase">By player</h2>
          <Bars rows={stats.by_player} label="name" testId="stats-by-player" />
        </div>
      </div>
    </PageShell>
  );
}
